type MaUrl = {
    id: string;
    name: string;
};

export type TruncatablePage = {
    id: string;
    path: string | null | undefined;
    maUrl?: MaUrl | null;
};

export type TruncateResult<T extends TruncatablePage> = {
    visiblePages: T[];
    hiddenPages: T[];
};

/**
 * Sorts and truncates session pages for display, prioritizing MA URL pages above the fold.
 *
 * Rules:
 * - MA URL pages are sorted to appear first
 * - When truncating, MA URL pages take priority in visible slots
 * - Remaining slots show first and last non-MA URL pages for context
 */
export function truncateSessionPages<T extends TruncatablePage>(
    pages: T[],
    maxDisplayed = 3
): TruncateResult<T> {
    // Sort pages so MA URL pages appear first
    const sortedPages = [...pages].sort((a, b) => {
        if (a.maUrl && !b.maUrl) return -1;
        if (!a.maUrl && b.maUrl) return 1;

        return 0;
    });

    // No truncation needed
    if (pages.length <= maxDisplayed) {
        return {
            visiblePages: sortedPages,
            hiddenPages: [],
        };
    }

    const maUrlPages = sortedPages.filter((page) => page.maUrl);
    const nonMaUrlPages = sortedPages.filter((page) => !page.maUrl);

    // All visible slots taken by MA URL pages
    if (maUrlPages.length >= maxDisplayed) {
        return {
            visiblePages: maUrlPages.slice(0, maxDisplayed),
            hiddenPages: [...maUrlPages.slice(maxDisplayed), ...nonMaUrlPages],
        };
    }

    // Show all MA URL pages + fill remaining slots with non-MA URL pages
    const remainingSlots = maxDisplayed - maUrlPages.length;
    const visiblePages: T[] = [...maUrlPages];
    let hiddenPages: T[] = [];

    if (remainingSlots >= 2 && nonMaUrlPages.length >= 2) {
        // Show first (remainingSlots - 1) pages and last page
        const firstPagesCount = Math.min(remainingSlots - 1, nonMaUrlPages.length - 1);
        visiblePages.push(...nonMaUrlPages.slice(0, firstPagesCount));
        visiblePages.push(nonMaUrlPages[nonMaUrlPages.length - 1] as T);
        hiddenPages = nonMaUrlPages.slice(firstPagesCount, nonMaUrlPages.length - 1);
    } else if (remainingSlots >= 1 && nonMaUrlPages.length >= 1) {
        visiblePages.push(nonMaUrlPages[0] as T);
        hiddenPages = nonMaUrlPages.slice(1);
    } else {
        hiddenPages = nonMaUrlPages;
    }

    return {visiblePages, hiddenPages};
}
